import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, TextField, MenuItem, Grid, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import ErpEmployeeFormFields, {
  emptyEmployeeForm,
  applyEmployeeFieldChange,
  toEmployeePayload,
  employeeFormatErrors,
} from './ErpEmployeeFormFields';

/**
 * Creación manual de ficha (spec 3.5): elegir la cotización de la que cuelga
 * y cargar los datos personales. Los documentos se adjuntan en el detalle.
 */
const ErpEmployeeWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();

  const [quotations, setQuotations] = useState([]);
  const [quotationId, setQuotationId] = useState('');
  const [form, setForm] = useState(emptyEmployeeForm);
  const [saving, setSaving] = useState(false);
  // RUT con otra ficha en curso: { name } mientras se muestra la confirmación
  const [rutConfirm, setRutConfirm] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/erp/quotations', {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        // Las subcotizaciones (COT-0009-1…) no se eligen aquí: la ficha va a
        // la cotización del cargo y la sub se asigna al pasar a contrato
        setQuotations(
          (response.data.response?.data || []).filter(
            (q) => ['quoted', 'in_use'].includes(q.status) && !q.parentQuotationId
          )
        );
      } catch (err) {
        console.error(err);
        showAlert(t('erp.quotations.loadError'), 'error');
      }
    };
    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const handleFieldChange = (path, value) =>
    setForm((prev) => applyEmployeeFieldChange(prev, path, value));

  const handleSave = async (confirmDuplicate = false) => {
    if (!quotationId || !form.personal.names.trim()) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }
    if (Object.keys(employeeFormatErrors(form)).length > 0) {
      showAlert(t('erp.validation.fixErrors'), 'warning');
      return;
    }
    try {
      setSaving(true);
      const response = await api.post(
        '/erp/employee-records',
        { organizationId: organization, quotationId, confirmDuplicate, ...toEmployeePayload(form) },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.employees.createSuccess'), 'success');
        const uid = response.data.response.data?.uid;
        navigate(uid ? `/erp/employees/${uid}` : '/erp/employees');
      } else {
        showAlert(t('erp.employees.saveError'), 'error');
      }
    } catch (err) {
      const code = err.response?.data?.response?.code;
      const existingName = err.response?.data?.response?.data?.existing?.names || '';
      if (code === 'RECORD_RUT_IN_PROGRESS') {
        // Mismo RUT con otra ficha en curso: advertir y permitir continuar
        // (la recontratación es legítima; el control de causales suma por RUT).
        // Se confirma en un modal; "Crear igual" reenvía con confirmDuplicate.
        setRutConfirm({ name: existingName });
      } else if (code === 'RECORD_DUPLICATE_QUOTATION') {
        showAlert(t('erp.employees.rutDuplicateQuotation', { name: existingName }), 'error');
      } else {
        console.error(err);
        showAlert(t('erp.employees.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/erp/employees')} sx={{ textTransform: 'none', mb: 1 }}>
        {t('erp.employees.title')}
      </Button>

      <Typography variant="h4" fontWeight={700}>{t('erp.employees.createTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('erp.employees.createSubtitle')}
      </Typography>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" required label={t('erp.employees.form.quotation')}
              value={quotationId} onChange={(e) => setQuotationId(e.target.value)}>
              {quotations.map((q) => (
                <MenuItem key={q.uid} value={q.uid}>
                  {q.quotationNumber ? `${q.quotationNumber} · ` : ''}{q.clientName} — {q.jobTitleName} ({q.period?.startDate})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {quotations.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>{t('erp.employees.noQuotations')}</Alert>
        )}

        <ErpEmployeeFormFields form={form} onChange={handleFieldChange} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button onClick={() => navigate('/erp/employees')} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" disabled={saving || Object.keys(employeeFormatErrors(form)).length > 0} onClick={() => handleSave(false)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
            {saving ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </Box>
      </Paper>

      {/* Mismo RUT con otra ficha en curso: confirmar antes de crear */}
      <Dialog open={Boolean(rutConfirm)} onClose={() => setRutConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.employees.rutInProgressTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('erp.employees.rutInProgressConfirm', { name: rutConfirm?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRutConfirm(null)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saving}
            onClick={() => { setRutConfirm(null); handleSave(true); }}
            sx={{ textTransform: 'none' }}
          >
            {t('erp.employees.rutInProgressCreate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpEmployeeWizard;
